import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { BarChart2, Download, Search } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { Button } from "../components/ui/button";
import SelectField from "../components/ui/SelectField";

const API_URL = process.env.REACT_APP_API_URL;

const PAGE_SIZE = 25;

const firstOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const today = () => new Date().toISOString().slice(0, 10);

const exportToExcel = (items, filename) => {
  const headers = [
    "Orden",
    "Fecha",
    "Cliente",
    "Propietario / Convenio",
    "Paciente",
    "Exámenes",
    "Cant. Exámenes",
    "Costo Total",
    "Facturado",
    "# Factura",
    "Fecha Factura",
  ];

  const esc = (v) =>
    String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  const rows = items
    .map(
      (row) => `<tr>
        <td>${esc(row.paciente_cod)}</td>
        <td>${esc(row.fecha)}</td>
        <td>${esc(row.cliente)}</td>
        <td>${esc(row.propietario)}</td>
        <td>${esc(row.paciente)}</td>
        <td>${esc(row.examenes)}</td>
        <td x:num>${esc(row.total_examenes || 0)}</td>
        <td x:num>${Number(row.costo_total || 0).toFixed(2)}</td>
        <td>${row.facturado ? "Sí" : "No"}</td>
        <td>${esc(row.factura || "")}</td>
        <td>${esc(row.fecha_factura ? String(row.fecha_factura).slice(0, 10) : "")}</td>
      </tr>`,
    )
    .join("");

  const html = `<html xmlns:x="urn:schemas-microsoft-com:office:excel">
<head><meta charset="UTF-8"/></head>
<body><table>
  <thead><tr>${headers.map((h) => `<th><b>${h}</b></th>`).join("")}</tr></thead>
  <tbody>${rows}</tbody>
</table></body></html>`;

  const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const IndicadoresPage = () => {
  const token = localStorage.getItem("token");

  const [dateRange, setDateRange] = useState({
    startDate: firstOfMonth(),
    endDate: today(),
  });
  const [targetType, setTargetType] = useState("");
  const [targetCode, setTargetCode] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [clientes, setClientes] = useState([]);
  const [propietarios, setPropietarios] = useState([]);

  const options =
    targetType === "cliente"
      ? clientes
      : targetType === "propietario"
        ? propietarios
        : [];

  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const paginatedItems = useMemo(
    () => items.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [items, currentPage],
  );

  const totals = useMemo(
    () => ({
      ordenes: items.length,
      costo: items.reduce((acc, r) => acc + Number(r.costo_total || 0), 0),
      examenes: items.reduce(
        (acc, r) => acc + Number(r.total_examenes || 0),
        0,
      ),
      facturadas: items.filter((r) => r.facturado).length,
    }),
    [items],
  );

  useEffect(() => {
    const load = async () => {
      try {
        const [cliRes, propRes] = await Promise.all([
          axios.get(`${API_URL}/util/clientes`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_URL}/propietarios`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        setClientes(Array.isArray(cliRes.data) ? cliRes.data : []);
        setPropietarios(Array.isArray(propRes.data) ? propRes.data : []);
      } catch {}
    };
    load();
  }, [token]); // eslint-disable-line

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      };
      if (targetType && targetCode) {
        params.targetType = targetType;
        params.targetCode = targetCode;
      }
      const { data } = await axios.get(
        `${API_URL}/indicadores/ordenes-convenio`,
        { headers: { Authorization: `Bearer ${token}` }, params },
      );
      setItems(data.items || []);
      setCurrentPage(1);
      if (!data.items?.length) toast.info("Sin datos para los filtros seleccionados");
    } catch (error) {
      toast.error(
        error.response?.data?.error || "No se pudieron cargar los datos",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!items.length) {
      toast.warning("No hay datos para descargar");
      return;
    }
    exportToExcel(
      items,
      `ordenes-convenio-${dateRange.startDate}-${dateRange.endDate}.xls`,
    );
  };

  const inputCls =
    "w-full border border-brand-200 rounded-xl px-3 py-2 text-sm bg-brand-50 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition-all";

  return (
    <AppLayout>
      <div className="p-6 space-y-5">
        {/* Cabecera */}
        <div className="flex items-center gap-3">
          <div className="p-2 bg-brand-100 rounded-xl">
            <BarChart2 className="h-5 w-5 text-brand-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Indicadores</h1>
            <p className="text-sm text-gray-500">
              Métricas y reportes de gestión
            </p>
          </div>
        </div>

        {/* Sección: Órdenes por Convenio */}
        <div className="bg-white rounded-2xl border border-brand-100 shadow-card p-5">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div>
              <h2 className="text-base font-semibold text-gray-800">
                Órdenes y Exámenes por Convenio
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Filtra por rango de fechas y opcionalmente por cliente o
                convenio
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleDownload}
              disabled={!items.length}
              className="gap-2 text-xs"
            >
              <Download className="h-3.5 w-3.5" /> Descargar Excel
            </Button>
          </div>

          {/* Filtros */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Fecha inicio
              </label>
              <input
                type="date"
                className={inputCls}
                value={dateRange.startDate}
                onChange={(e) =>
                  setDateRange((p) => ({ ...p, startDate: e.target.value }))
                }
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Fecha fin
              </label>
              <input
                type="date"
                className={inputCls}
                value={dateRange.endDate}
                onChange={(e) =>
                  setDateRange((p) => ({ ...p, endDate: e.target.value }))
                }
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Filtrar por
              </label>
              <select
                className={inputCls}
                value={targetType}
                onChange={(e) => {
                  setTargetType(e.target.value);
                  setTargetCode("");
                }}
              >
                <option value="">Todos</option>
                <option value="cliente">Cliente</option>
                <option value="propietario">Convenio</option>
              </select>
            </div>
            {targetType ? (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {targetType === "cliente" ? "Cliente" : "Convenio"}
                </label>
                <SelectField
                  key={targetType}
                  name="targetCode"
                  value={targetCode}
                  options={options}
                  valueKey={targetType === "cliente" ? "clte_codigo" : "nit"}
                  displayFn={(opt) =>
                    targetType === "cliente"
                      ? `${opt.clte_codigo} - ${opt.razon || opt.nombre}`
                      : `${opt.nit} - ${opt.nom1 || ""} ${opt.ape1 || ""}`
                  }
                  placeholder="Buscar..."
                  onChange={(e) => setTargetCode(e.target.value)}
                />
              </div>
            ) : (
              <div />
            )}
          </div>

          <div className="flex justify-end mb-4">
            <Button onClick={fetchData} disabled={loading} className="gap-2">
              {loading ? (
                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              {loading ? "Cargando..." : "Consultar"}
            </Button>
          </div>

          {/* Resumen */}
          {items.length > 0 && (
            <div className="flex flex-wrap gap-3 mb-4">
              {[
                { label: "Total órdenes", value: totals.ordenes },
                { label: "Total exámenes", value: totals.examenes },
                {
                  label: "Costo total",
                  value: `$${totals.costo.toLocaleString("es-CO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                },
                {
                  label: "Facturadas",
                  value: `${totals.facturadas} / ${totals.ordenes}`,
                },
              ].map(({ label, value }) => (
                <div
                  key={label}
                  className="bg-brand-50 border border-brand-100 rounded-xl px-4 py-2 text-sm"
                >
                  <span className="text-gray-500">{label}: </span>
                  <strong className="text-brand-700">{value}</strong>
                </div>
              ))}
            </div>
          )}

          {/* Tabla */}
          <div className="overflow-x-auto rounded-xl border border-brand-100">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-brand-gradient text-white">
                  {[
                    "Orden",
                    "Fecha",
                    "Cliente",
                    "Propietario / Convenio",
                    "Paciente",
                    "Exámenes",
                    "# Exáms",
                    "Costo Total",
                    "Facturado",
                    "# Factura",
                    "Fecha Factura",
                  ].map((h, i) => (
                    <th
                      key={h}
                      className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider whitespace-nowrap ${i === 6 || i === 7 ? "text-right" : "text-left"}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-50">
                {loading ? (
                  <tr>
                    <td colSpan="11" className="py-12 text-center">
                      <div className="flex flex-col items-center gap-3 text-brand-400">
                        <div className="h-7 w-7 border-2 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
                        <span className="text-sm">Cargando datos...</span>
                      </div>
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td
                      colSpan="11"
                      className="py-12 text-center text-gray-400 text-sm"
                    >
                      Selecciona un rango de fechas y pulsa &quot;Consultar&quot;.
                    </td>
                  </tr>
                ) : (
                  paginatedItems.map((row, i) => (
                    <tr
                      key={`${row.paciente_cod}-${i}`}
                      className={`transition-colors ${i % 2 === 0 ? "bg-white" : "bg-brand-50/30"} hover:bg-brand-50`}
                    >
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs bg-brand-100 text-brand-700 px-2 py-0.5 rounded font-bold">
                          {row.paciente_cod}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {row.fecha}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {row.cliente || "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {row.propietario || "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {row.paciente || "—"}
                      </td>
                      <td
                        className="px-4 py-3 text-gray-600 max-w-xs"
                        title={row.examenes || ""}
                      >
                        <span className="text-xs line-clamp-2">
                          {row.examenes || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">
                        {row.total_examenes}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-brand-700">
                        $
                        {Number(row.costo_total || 0).toLocaleString("es-CO", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td className="px-4 py-3">
                        {row.facturado ? (
                          <span className="inline-flex text-xs font-semibold text-emerald-700 bg-emerald-100 border border-emerald-200 rounded-full px-2 py-0.5">
                            Sí
                          </span>
                        ) : (
                          <span className="inline-flex text-xs font-semibold text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5">
                            No
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {row.factura ? (
                          <span className="font-mono text-xs bg-brand-100 text-brand-700 px-2 py-0.5 rounded">
                            {row.factura}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap text-xs">
                        {row.fecha_factura
                          ? String(row.fecha_factura).slice(0, 10)
                          : "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {items.length > PAGE_SIZE && (
            <div className="flex flex-col sm:flex-row items-center justify-between mt-4 gap-3">
              <p className="text-xs text-gray-500 order-2 sm:order-1">
                Mostrando{" "}
                <strong>{(currentPage - 1) * PAGE_SIZE + 1}</strong>–
                <strong>{Math.min(currentPage * PAGE_SIZE, items.length)}</strong>{" "}
                de <strong>{items.length}</strong> registros
              </p>
              <div className="flex items-center gap-2 order-1 sm:order-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-xs rounded-lg border border-brand-200 text-brand-700 disabled:opacity-40 hover:bg-brand-50 transition-colors"
                >
                  Anterior
                </button>
                <span className="text-xs text-gray-600 font-medium">
                  {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 text-xs rounded-lg border border-brand-200 text-brand-700 disabled:opacity-40 hover:bg-brand-50 transition-colors"
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default IndicadoresPage;
